import React, { useEffect } from 'react';
import Hero from './components/Hero';
import Navigation from './components/Navigation';
import Home from './pages/Home';
import Specialized from './pages/Specialized';
import Batch from './pages/Batch';
import Countdown from './pages/Countdown';
import TimeZones from './pages/TimeZones';
import Lists from './pages/Lists';
import DateTools from './pages/DateTools';
import NotFound from './pages/NotFound';
import Reference from './pages/Reference';
import { PrivacyPolicy, TermsOfService, CookiePolicy, LegalNotice } from './pages/LegalDocs';
import { Link, usePathname, useDocumentHead } from './router';
import { findRoute } from './routes.js';

const views: Record<string, React.ReactNode> = {
  HOME: <Home />,
  SPECIALIZED: <Specialized />,
  BATCH: <Batch />,
  COUNTDOWN: <Countdown />,
  TIMEZONES: <TimeZones />,
  LISTS: <Lists />,
  DATE_TOOLS: <DateTools />,
  LEGAL: <LegalNotice />,
  PRIVACY: <PrivacyPolicy />,
  TERMS: <TermsOfService />,
  COOKIES: <CookiePolicy />,
};

function App() {
  const pathname = usePathname();
  const route = findRoute(pathname);

  useDocumentHead(pathname);

  // Scroll to top on navigation, but leave the initial load alone so deep
  // links and refreshes keep the browser's own scroll restoration.
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <Hero />
      <Navigation pathname={pathname} />

      <main className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {route ? (
          <>
            {route.heading && (
              <h1 className="text-2xl font-bold text-slate-900 mb-6">{route.heading}</h1>
            )}
            {route.view === 'REFERENCE' ? <Reference route={route} /> : views[route.view]}
          </>
        ) : (
          <NotFound />
        )}
      </main>

      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">

            {/* Copyright & Info */}
            <div className="text-center md:text-left">
              <p className="text-slate-900 font-semibold text-sm">EpochTimer</p>
              <p className="text-slate-500 text-xs mt-1">
                © {new Date().getFullYear()} All calculations performed client-side.
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-600 font-medium">
              <Link href="/legal" className="hover:text-sky-600 transition-colors">Legal</Link>
              <Link href="/privacy" className="hover:text-sky-600 transition-colors">Privacy</Link>
              <Link href="/terms" className="hover:text-sky-600 transition-colors">Terms</Link>
              <Link href="/cookies" className="hover:text-sky-600 transition-colors">Cookies</Link>
            </div>

            {/* Ad / Partner */}
            <div className="text-center md:text-right">
               <a
                href="https://image2editor.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors text-sm font-medium"
              >
                <span>Edit Images at Image2Editor.com</span>
              </a>
            </div>

          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
